import type {
  ClassifyResult,
  ErrorTagsRecord,
  RateLimitScope,
  ReactQueryCacheMeta,
  ReactQueryMutationMeta,
  ReactQueryTagExtras,
  ReportingLevel,
  UnknownRecord,
} from "@/types";
import {
  AUTH_ERROR_REPORTING_LEVEL_MAP,
  getHttpStatusFromSupabase,
  isFunctionsHttpError,
  isPostgrestError,
  isSupabaseAuthError,
} from "../supabase";
import {
  buildErrorTagsBase,
  composeRateLimitPrefix,
  DROP_4XX,
  getHttpStatus,
  isAbortOrCancel,
  isOfflineOrNetwork,
} from "./common";

// Build react-query rate-limit key (scoped by cache/mutation + query/mutation profile)
export function buildRateLimitKey(
  scope: RateLimitScope,
  verdict: ClassifyResult,
  error: unknown,
  extras?: {
    query?: unknown;
    mutation?: unknown;
    queryHash?: string;
    mutationKey?: unknown;
  },
): string {
  if (isOfflineOrNetwork(error)) return "offline";
  const prefix = composeRateLimitPrefix(verdict, error);
  if (scope === "cache") {
    const qh =
      extras?.queryHash ??
      (extras?.query as { queryHash?: string } | undefined)?.queryHash ??
      "";
    return `rq:${scope}:${prefix}:${String(qh)}`;
  }
  const mk =
    extras?.mutationKey ??
    (extras?.mutation as { options?: { mutationKey?: unknown } } | undefined)
      ?.options?.mutationKey;
  const mkeyStr = Array.isArray(mk) ? mk.join(":") : String(mk ?? "");
  return `rq:${scope}:${prefix}:${mkeyStr}`;
}

function extractQueryHash(query?: unknown): string | undefined {
  return (query as { queryHash?: string } | undefined)?.queryHash || undefined;
}

function extractMutationKey(mutation?: unknown): string | string[] | undefined {
  const mk = (mutation as { options?: { mutationKey?: unknown } } | undefined)
    ?.options?.mutationKey;
  if (Array.isArray(mk)) return mk;

  return mk !== undefined && mk !== null ? String(mk) : undefined;
}

// Build standard error tags for react-query operations
export function buildErrorTags(
  scope: RateLimitScope,
  verdict: ClassifyResult,
  extras?: ReactQueryTagExtras,
): ErrorTagsRecord {
  return buildErrorTagsBase("react-query", scope, verdict, extras);
}

// Extract lightweight metadata for cache error reporting
export function buildCacheMeta(
  query?: unknown,
  details?: UnknownRecord,
): ReactQueryCacheMeta | undefined {
  const queryHash = extractQueryHash(query);
  const meta: ReactQueryCacheMeta = {};

  if (queryHash) meta.queryHash = queryHash;
  if (details) meta.details = details;

  return Object.keys(meta).length > 0 ? meta : undefined;
}

// Extract lightweight metadata for mutation error reporting
export function buildMutationMeta(
  mutation?: unknown,
  details?: UnknownRecord,
): ReactQueryMutationMeta | undefined {
  const mutationKey = extractMutationKey(mutation);
  const variables = (
    mutation as { state?: { variables?: unknown } } | undefined
  )?.state?.variables;
  const meta: ReactQueryMutationMeta = {};

  if (mutationKey !== undefined) meta.mutationKey = mutationKey;
  if (variables !== undefined) meta.variables = variables;
  if (details) meta.details = details;

  return Object.keys(meta).length > 0 ? meta : undefined;
}

function coerceDecision(val: unknown): ReportingLevel | undefined {
  return val === "drop" || val === "info" || val === "warn" || val === "error"
    ? val
    : undefined;
}

// Read per-operation reporting override from react-query meta
export function getReportOverride(
  scope: RateLimitScope,
  opts: { query?: unknown; mutation?: unknown },
): ReportingLevel | undefined {
  if (scope === "cache") {
    const rep = (
      opts.query as { options?: { meta?: { report?: unknown } } } | undefined
    )?.options?.meta?.report;
    return coerceDecision(rep);
  }

  const rep = (
    opts.mutation as { options?: { meta?: { report?: unknown } } } | undefined
  )?.options?.meta?.report;
  return coerceDecision(rep);
}

// Classify react-query surfaced errors into reporting levels and optional status
export function classifyReactQueryError(error: unknown): ClassifyResult {
  // Abort/cancel
  if (isAbortOrCancel(error)) return { level: "drop" };

  // AuthApiError → decision by code, fallback by status
  if (isSupabaseAuthError(error)) {
    const code =
      typeof (error as { code?: unknown }).code === "string"
        ? (error as { code: string }).code
        : undefined;
    const mapped = code ? AUTH_ERROR_REPORTING_LEVEL_MAP[code] : undefined;
    if (mapped)
      return { level: mapped, status: getHttpStatusFromSupabase(error) };

    const s = getHttpStatusFromSupabase(error);
    if (s !== undefined) {
      if (s >= 500) return { level: "error", status: s };
      if (DROP_4XX.has(s)) return { level: "drop", status: s };

      return { level: "warn", status: s };
    }
  }

  // Functions* errors
  // Functions Error Handling: https://supabase.com/docs/guides/functions/error-handling
  const name = (error as { name?: string })?.name || "";
  if (name === "FunctionsRelayError" || name === "FunctionsFetchError")
    return { level: "info" };

  if (isFunctionsHttpError(error)) {
    const s = getHttpStatusFromSupabase(error);
    if (s !== undefined) {
      if (s >= 500) return { level: "error", status: s };
      if (DROP_4XX.has(s)) return { level: "drop", status: s };

      return { level: "warn", status: s };
    }
  }

  // PostgrestError → treat as application error
  if (isPostgrestError(error)) {
    return { level: "error" };
  }

  // Generic HTTP classification
  const s = getHttpStatus(error);
  if (s !== undefined) {
    if (s >= 500) return { level: "error", status: s };
    if (DROP_4XX.has(s)) return { level: "drop", status: s };

    return { level: "warn", status: s };
  }

  // Network/offline
  if (isOfflineOrNetwork(error)) return { level: "info" };

  // Fallback
  return { level: "warn" };
}
